import React from 'react';
import { motion } from 'framer-motion';
import { 
    Trophy, Star, TrendingUp, TrendingDown, Minus, Target, 
    Award, Zap, Calendar, BookOpen, Eye, Ear, Users, 
    FileCheck, Rocket, Handshake, CheckCircle, Circle, Gem
} from 'lucide-react';

// Professional Level Badge Component
export const LevelBadge = ({ level, size = 'md', showProgress = false }) => {
    const sizeClasses = {
        sm: 'h-6 px-2 text-xs',
        md: 'h-8 px-3 text-sm',
        lg: 'h-10 px-4 text-base'
    };

    const iconSize = {
        sm: 14,
        md: 18,
        lg: 22
    };

    const icons = {
        essentiel: Circle,
        confirme: CheckCircle,
        expert: Star,
        reference: Award,
        elite: Gem
    };

    const Icon = icons[level?.level_id] || Circle;

    return (
        <div className="inline-flex flex-col items-center gap-1">
            <div 
                className={`inline-flex items-center gap-2 rounded-full font-semibold ${sizeClasses[size]}`}
                style={{ 
                    backgroundColor: `${level?.color}20`,
                    color: level?.color,
                    border: `2px solid ${level?.color}`
                }}
            >
                <Icon size={iconSize[size]} />
                <span>{level?.name || 'Essentiel'}</span>
            </div>
            {showProgress && level?.next_level && (
                <div className="w-full">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{level.name}</span>
                        <span>{level.next_level}</span>
                    </div>
                    <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full rounded-full"
                            style={{ backgroundColor: level.color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${level.progress_percent}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 text-center">
                        {level.points_to_next} pts pour {level.next_level}
                    </p>
                </div>
            )}
        </div>
    );
};

// Confidence Index Display
export const ConfidenceIndex = ({ index, size = 'md', showBreakdown = false }) => {
    const getColor = (value) => {
        if (value >= 85) return '#10B981';
        if (value >= 70) return '#3B82F6';
        if (value >= 50) return '#F59E0B';
        return '#EF4444';
    };

    const getLabel = (value) => {
        if (value >= 85) return 'Excellent';
        if (value >= 70) return 'Bon';
        if (value >= 50) return 'Correct';
        return 'À améliorer';
    };

    const color = getColor(index?.index || 0);
    const value = index?.index || 0;

    const sizeConfig = {
        sm: { width: 60, strokeWidth: 6, fontSize: 14 },
        md: { width: 100, strokeWidth: 8, fontSize: 20 },
        lg: { width: 140, strokeWidth: 10, fontSize: 28 }
    };

    const config = sizeConfig[size];
    const radius = (config.width - config.strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div className="flex flex-col items-center gap-3">
            <div className="relative" style={{ width: config.width, height: config.width }}>
                {/* Background circle */}
                <svg className="transform -rotate-90" width={config.width} height={config.width}>
                    <circle
                        cx={config.width / 2}
                        cy={config.width / 2}
                        r={radius}
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth={config.strokeWidth}
                        fill="none"
                    />
                    <motion.circle
                        cx={config.width / 2}
                        cy={config.width / 2}
                        r={radius}
                        stroke={color}
                        strokeWidth={config.strokeWidth}
                        fill="none"
                        strokeLinecap="round"
                        initial={{ strokeDashoffset: circumference }}
                        animate={{ strokeDashoffset: offset }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        style={{
                            strokeDasharray: circumference,
                        }}
                    />
                </svg>
                {/* Value in center */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.span 
                        className="font-bold"
                        style={{ fontSize: config.fontSize, color }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        {Math.round(value)}
                    </motion.span>
                    {size !== 'sm' && (
                        <span className="text-xs text-muted-foreground">/100</span>
                    )}
                </div>
            </div>
            <div className="text-center">
                <span 
                    className="font-medium text-sm"
                    style={{ color }}
                >
                    {getLabel(value)}
                </span>
                <p className="text-[10px] text-muted-foreground mt-1 max-w-[200px]">IndiceHomly</p>
            </div>

            {/* Explanation */}
            {size !== 'sm' && (
                <div className="w-full mt-2 p-3 bg-white/5 rounded-lg border border-white/10">
                    <p className="text-xs font-medium text-foreground mb-1.5">IndiceHomly vs HomlyScore</p>
                    <div className="space-y-1.5 text-[11px] text-muted-foreground leading-relaxed">
                        <p><span className="text-[#FF914D] font-medium">HomlyScore</span> = votre niveau global. Il monte avec les bonnes notes vendeurs, les certifications réussies et les ventes conclues. C'est votre classement.</p>
                        <p><span style={{color}} className="font-medium">IndiceHomly</span> = votre fiabilité au quotidien. Il mesure si vous répondez vite, si vous tenez vos vendeurs informés et si vos mandats sont suivis régulièrement.</p>
                    </div>
                </div>
            )}
            
            {showBreakdown && index?.breakdown && (
                <div className="w-full mt-4 space-y-2">
                    {Object.entries(index.breakdown).map(([key, val]) => {
                        const labels = {
                            closing_ratings: 'Notes vendeurs',
                            reliability: 'Fiabilité suivi',
                            checkpoints: 'Retours vendeurs',
                            badges: 'Badges obtenus',
                            response_time: 'Réactivité'
                        };
                        return (
                            <div key={key} className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground w-28">
                                    {labels[key] || key.replace(/_/g, ' ')}
                                </span>
                                <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full rounded-full"
                                        style={{ backgroundColor: getColor(val) }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${val}%` }}
                                        transition={{ duration: 0.6, delay: 0.1 }}
                                    />
                                </div>
                                <span className="text-xs text-foreground w-8">{Math.round(val)}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Badge Component
export const Badge = ({ badge, size = 'md', showDetails = false }) => {
    const iconMap = {
        'zap': Zap,
        'calendar-check': Calendar,
        'book-open': BookOpen,
        'eye': Eye,
        'ear': Ear,
        'users': Users,
        'trending-up': TrendingUp,
        'handshake': Handshake,
        'file-check': FileCheck,
        'rocket': Rocket,
        'trophy': Trophy,
        'award': Award,
        'star': Star
    };

    const Icon = iconMap[badge?.icon] || Star;
    const tier = badge?.tier || {};

    const sizeConfig = {
        sm: { container: 'w-12 h-12', icon: 16, text: 'text-xs' },
        md: { container: 'w-16 h-16', icon: 24, text: 'text-sm' },
        lg: { container: 'w-20 h-20', icon: 32, text: 'text-base' }
    };

    const config = sizeConfig[size];

    return (
        <motion.div 
            className="flex flex-col items-center gap-2"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
        >
            <div 
                className={`${config.container} rounded-2xl flex items-center justify-center relative`}
                style={{
                    background: `linear-gradient(135deg, ${tier.color}30, ${tier.color}10)`,
                    border: `2px solid ${tier.border_color || tier.color}`,
                    boxShadow: tier.glow ? `0 0 20px ${tier.color}40` : 'none'
                }}
            >
                <Icon size={config.icon} style={{ color: tier.color }} />
                {badge?.is_verified && (
                    <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                        <CheckCircle size={12} className="text-foreground" />
                    </div>
                )}
            </div>
            <div className="text-center">
                <p className={`${config.text} font-medium text-gray-200`}>{badge?.name}</p>
                {showDetails && (
                    <>
                        <p className="text-xs text-muted-foreground mt-0.5">{badge?.description}</p>
                        <div 
                            className="text-xs font-medium mt-1 px-2 py-0.5 rounded-full inline-block"
                            style={{ 
                                backgroundColor: `${tier.color}20`,
                                color: tier.color 
                            }}
                        >
                            {tier.name} • {tier.rate}%
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
};

// Elo Score Display
export const EloDisplay = ({ elo, trend, label = "Score", size = 'md' }) => {
    const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
    const trendColor = trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : '#6B7280';

    const sizeConfig = {
        sm: { score: 'text-xl', label: 'text-xs' },
        md: { score: 'text-3xl', label: 'text-sm' },
        lg: { score: 'text-4xl', label: 'text-base' }
    };

    const config = sizeConfig[size];

    return (
        <div className="flex flex-col items-center">
            <span className={`text-muted-foreground ${config.label}`}>{label}</span>
            <div className="flex items-center gap-2">
                <motion.span 
                    className={`font-bold text-white ${config.score}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {Math.round(elo || 0)}
                </motion.span>
                {trend && (
                    <TrendIcon size={20} style={{ color: trendColor }} />
                )}
            </div>
        </div>
    );
};

// Mission Card
export const MissionCard = ({ mission, onCheck }) => {
    const difficultyColors = {
        easy: '#10B981',
        medium: '#F59E0B',
        hard: '#EF4444'
    };

    const difficultyLabels = {
        easy: 'Facile',
        medium: 'Moyen',
        hard: 'Difficile'
    };

    return (
        <motion.div 
            className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-border/50"
            whileHover={{ scale: 1.02, borderColor: 'rgba(139, 92, 246, 0.5)' }}
        >
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                    <Target size={18} className="text-purple-400" />
                    <h3 className="font-semibold text-white">{mission.name}</h3>
                </div>
                <span 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ 
                        backgroundColor: `${difficultyColors[mission.difficulty]}20`,
                        color: difficultyColors[mission.difficulty]
                    }}
                >
                    {difficultyLabels[mission.difficulty]}
                </span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{mission.description}</p>
            
            {/* Progress bar */}
            <div className="mb-3">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progression</span>
                    <span>{mission.progress || 0}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${mission.progress || 0}%` }}
                        transition={{ duration: 0.6 }}
                    />
                </div>
            </div>
            
            {/* Rewards */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {mission.rewards?.xp && (
                        <span className="text-xs flex items-center gap-1 text-yellow-400">
                            <Zap size={12} />
                            +{mission.rewards.xp} XP
                        </span>
                    )}
                    {mission.rewards?.elo_season_bonus && (
                        <span className="text-xs flex items-center gap-1 text-purple-400">
                            <TrendingUp size={12} />
                            +{mission.rewards.elo_season_bonus} Elo
                        </span>
                    )}
                </div>
                {mission.completed && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Complété
                    </span>
                )}
            </div>
        </motion.div>
    );
};

// Certification Card
export const CertificationCard = ({ certification, onStart, isPassed, canRetake }) => {
    return (
        <motion.div 
            className={`rounded-xl p-5 border ${
                isPassed 
                    ? 'bg-green-900/20 border-green-500/30' 
                    : 'bg-gray-800/50 border-border/50'
            }`}
            whileHover={{ scale: 1.02 }}
        >
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="font-semibold text-white text-lg">{certification.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{certification.description}</p>
                </div>
                {isPassed && (
                    <div className="bg-green-500/20 p-2 rounded-full">
                        <CheckCircle size={20} className="text-green-400" />
                    </div>
                )}
            </div>
            
            {/* Topics */}
            <div className="flex flex-wrap gap-2 mb-4">
                {certification.topics?.slice(0, 3).map((topic, i) => (
                    <span key={i} className="text-xs bg-gray-700/50 text-foreground px-2 py-1 rounded-full">
                        {topic}
                    </span>
                ))}
                {certification.topics?.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{certification.topics.length - 3}</span>
                )}
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                <span>{certification.questions_count} questions</span>
                <span>•</span>
                <span>{certification.duration_minutes} min</span>
                <span>•</span>
                <span>{certification.passing_score}% requis</span>
            </div>
            
            {/* Rewards */}
            <div className="flex items-center gap-3 mb-4">
                <span className="text-xs flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/20 text-purple-300">
                    <TrendingUp size={12} />
                    +{certification.rewards?.elo_bonus} Elo
                </span>
                {certification.rewards?.badge && (
                    <span className="text-xs flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-300">
                        <Award size={12} />
                        Badge
                    </span>
                )}
            </div>
            
            {/* Action */}
            <button
                onClick={() => onStart?.(certification.id)}
                disabled={isPassed && !canRetake}
                className={`w-full py-2.5 rounded-lg font-medium transition-all ${
                    isPassed && !canRetake
                        ? 'bg-gray-700 text-muted-foreground cursor-not-allowed'
                        : isPassed
                            ? 'bg-green-600 hover:bg-green-500 text-white'
                            : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                }`}
            >
                {isPassed ? (canRetake ? 'Repasser' : 'Réussi ✓') : 'Commencer'}
            </button>
        </motion.div>
    );
};

// Season Progress Card
export const SeasonProgress = ({ seasonData }) => {
    const currentLevel = seasonData?.season_level || 1;
    const currentXP = seasonData?.season_xp || 0;
    const xpToNext = seasonData?.xp_to_next_level || 100;
    const levelName = seasonData?.level_name || 'Débutant';
    
    const progress = xpToNext > 0 ? Math.min(100, (currentXP / (currentXP + xpToNext)) * 100) : 100;

    return (
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-5 border border-purple-500/20">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-muted-foreground text-sm">Saison en cours</h3>
                    <p className="text-white font-semibold text-lg">Niveau {currentLevel}: {levelName}</p>
                </div>
                <div className="text-right">
                    <span className="text-2xl font-bold text-white">{currentXP}</span>
                    <span className="text-muted-foreground text-sm"> XP</span>
                </div>
            </div>
            
            {/* Progress to next level */}
            <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-2">
                    <span>Niveau {currentLevel}</span>
                    <span>Niveau {currentLevel + 1}</span>
                </div>
                <div className="h-3 bg-gray-700/50 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                    Encore {xpToNext} XP pour le niveau suivant
                </p>
            </div>
        </div>
    );
};

export default {
    LevelBadge,
    ConfidenceIndex,
    Badge,
    EloDisplay,
    MissionCard,
    CertificationCard,
    SeasonProgress
};
